
import * as THREE from 'three';
import { CurtainWallElement } from './types';

export class CurtainWallSolver {

    /**
     * Generate the complete hierarchy for a Curtain Wall:
     * - Panels (Glass)
     * - Mullions (Frames)
     * - Grids (Lines - Optional helpers)
     */
    static generateCurtainWall(wall: CurtainWallElement): THREE.Group {
        const group = new THREE.Group();
        group.name = "CurtainWall_Aggregate";

        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const height = wall.geometryData.height;

        // Basis Vectors
        const diff = new THREE.Vector3().subVectors(end, start);
        const length = diff.length();
        const dir = diff.clone().normalize();
        const up = new THREE.Vector3(0, 0, 1);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _normal = new THREE.Vector3().crossVectors(dir, up).normalize();

        // 1. Prepare Grid Arrays (Add Boundaries)
        // Sort and ensure boundaries act as grids (0 and Length/Height)
        const vGrids = [...(wall.geometryData.verticalGrids || [])].sort((a, b) => a - b);
        if (vGrids[0] !== 0) vGrids.unshift(0);
        if (vGrids[vGrids.length - 1] !== length) vGrids.push(length);

        const hGrids = [...(wall.geometryData.horizontalGrids || [])].sort((a, b) => a - b);
        if (hGrids[0] !== 0) hGrids.unshift(0);
        if (hGrids[hGrids.length - 1] !== height) hGrids.push(height);

        // Mullion Dimensions. Width honors the per-wall override written by
        // the Mullion tool from the Options Bar curtainMullionSize; default
        // stays 50 mm.
        const mWidth = ((wall.geometryData as any).mullionWidth as number | undefined) ?? 0.050;
        const mDepth = 0.150; // 150mm
        const pThick = 0.025; // 25mm Glass

        // 2. Generate Mullions (Vertical)
        // Placed at each vGrid
        vGrids.forEach(v => {
            const pos = start.clone().add(dir.clone().multiplyScalar(v));
            const mullionGeo = new THREE.BoxGeometry(mWidth, mDepth, height);
            // Center is at (0,0,0) -> moved to (0,0, height/2)
            mullionGeo.translate(0, 0, height / 2);

            const mesh = new THREE.Mesh(mullionGeo, this.getMullionMaterial());
            mesh.position.copy(pos);
            mesh.lookAt(pos.clone().add(dir)); // Align X with wall dir? No, Box is axis aligned initially.
            // With lookAt(dir), Z becomes the direction. 
            // We want Box Depth along Normal. Box Width along Dir. Box Height along Z.
            // BoxGeometry defaults: X=Width, Y=Height, Z=Depth.
            // ThreeJS: Y is Up usually, but we work Z up.
            // Let's assume standard BoxGeometry construction is width(X), height(Y), depth(Z).
            // We want Z axis of box geometry along World Z.

            // Re-construct geometry explicitly for Z-Up
            // Box(width, depth, height) -> X, Y, Z
            const g = new THREE.BoxGeometry(mWidth, mDepth, height);
            g.translate(0, 0, height / 2);

            const m = new THREE.Mesh(g, this.getMullionMaterial());
            m.position.copy(pos);

            // Rotate to align with Wall Direction
            // Initial Box X is aligned with World X.
            // Wall Dir angle:
            const angle = Math.atan2(dir.y, dir.x);
            m.rotation.z = angle;

            group.add(m);
        });

        // 3. Generate Mullions (Horizontal)
        // Segments between Vertical Grids or Continuous?
        // Let's do Continuous Horizontal for simplicity, intersecting verticals.
        hGrids.forEach(h => {
            // Bar from Start to End at height h
            // Skip 0 and Height if we want "frame" look (already covered by verticals? no)
            // Usually top/bottom rails exist.

            const centerline = start.clone().add(end).multiplyScalar(0.5).add(new THREE.Vector3(0, 0, h));

            const g = new THREE.BoxGeometry(length, mDepth, mWidth); // Width(X)=Length, Depth(Y)=Depth, Height(Z)=Width (visual thick)

            const m = new THREE.Mesh(g, this.getMullionMaterial());
            m.position.copy(centerline);

            const angle = Math.atan2(dir.y, dir.x);
            m.rotation.z = angle;

            group.add(m);
        });

        // 4. Generate Panels
        // In spaces between grids
        for (let i = 0; i < vGrids.length - 1; i++) {
            const v1 = vGrids[i];
            const v2 = vGrids[i + 1];

            for (let j = 0; j < hGrids.length - 1; j++) {
                const h1 = hGrids[j];
                const h2 = hGrids[j + 1];

                const panelW = (v2 - v1) - mWidth; // Deduct mullion overlap approx
                const panelH = (h2 - h1) - mWidth;

                if (panelW <= 0 || panelH <= 0) continue;

                // Center
                const midV = (v1 + v2) / 2;
                const midH = (h1 + h2) / 2;

                const pos = start.clone().add(dir.clone().multiplyScalar(midV));
                pos.z = midH;

                const g = new THREE.BoxGeometry(panelW, pThick, panelH);
                const p = new THREE.Mesh(g, this.getGlassMaterial());
                p.position.copy(pos);

                const angle = Math.atan2(dir.y, dir.x);
                p.rotation.z = angle;

                group.add(p);
            }
        }

        return group;
    }

    private static getMullionMaterial(): THREE.Material {
        return new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3, metalness: 0.8 });
    }

    private static getGlassMaterial(): THREE.Material {
        return new THREE.MeshPhysicalMaterial({
            color: 0x88ccff,
            transmission: 0.9,
            opacity: 0.5,
            transparent: true,
            roughness: 0,
            metalness: 0
        });
    }
}
